
import { useState, useEffect } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { UserProfile } from '@/lib/supabase';
import { localStorageCache } from '@/utils/cacheUtils';

const PROFILE_CACHE_KEY = 'auth:userProfile';
const PROFILE_CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours

export interface UseSupabaseAuthReturn {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isConfigured: boolean;
  authError: string | null;
}

export const useSupabaseAuth = (): UseSupabaseAuthReturn => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfigured, setIsConfigured] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Clear profile cache on logout
  const clearProfileCache = () => {
    localStorageCache.remove(PROFILE_CACHE_KEY);
  };

  useEffect(() => {
    console.log("Setting up auth state listener");
    
    // Check for an existing session first
    const getInitialSession = async () => {
      try {
        setIsLoading(true);
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error);
          setAuthError(error.message);
          if (error.message?.includes('not configured')) {
            setIsConfigured(false);
          }
          setIsLoading(false);
          return;
        } 
        
        console.log("Initial session check:", initialSession?.user?.id);
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        
        if (initialSession?.user) {
          // Try to get cached profile first
          const cachedProfile = localStorageCache.get<UserProfile>(PROFILE_CACHE_KEY);
          if (cachedProfile && cachedProfile.id === initialSession.user.id) {
            console.log("Using cached profile from initial session check");
            setProfile(cachedProfile);
            setIsLoading(false);
            
            // Still fetch fresh profile in background
            fetchUserProfile(initialSession.user.id, true);
          } else {
            // No valid cache, must wait for profile fetch
            await fetchUserProfile(initialSession.user.id);
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      } catch (error: any) {
        console.error("Error in getInitialSession:", error);
        setAuthError(error.message || "Failed to initialize authentication");
        setIsLoading(false);
      }
    };

    // Get initial session
    getInitialSession();
    
    // Then set up the auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log("Auth state changed:", event, currentSession?.user?.id);
        
        // Don't set isLoading=true on token refresh events
        if (event !== 'TOKEN_REFRESHED') {
          setIsLoading(true);
        }
        
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        // Clear cache on sign out
        if (event === 'SIGNED_OUT') {
          clearProfileCache();
          setProfile(null);
          setIsLoading(false);
          return;
        }
        
        // Only fetch profile if we have a user
        if (currentSession?.user) {
          setAuthError(null);
          
          // Try to get cached profile first
          const cachedProfile = localStorageCache.get<UserProfile>(PROFILE_CACHE_KEY);
          if (cachedProfile && cachedProfile.id === currentSession.user.id) {
            console.log("Using cached profile from auth state change");
            setProfile(cachedProfile);
            setIsLoading(false);
            
            // Refresh profile in background
            setTimeout(() => {
              fetchUserProfile(currentSession.user.id, true);
            }, 100);
          } else {
            // No cache, fetch profile and wait
            fetchUserProfile(currentSession.user.id).finally(() => {
              setIsLoading(false);
            });
          }
        } else if (event !== 'TOKEN_REFRESHED') {
          setIsLoading(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchUserProfile = async (userId: string, isBackgroundFetch = false) => {
    try {
      if (!isBackgroundFetch) {
        console.log("Fetching profile for user:", userId);
      }
      
      // Use direct Supabase query
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching user profile:', error);
        setAuthError(`Error fetching user profile: ${error.message}`);
        return;
      } 
      
      if (data) {
        console.log("Profile fetched successfully:", data.id);
        // Cache the profile data
        localStorageCache.set(PROFILE_CACHE_KEY, data, PROFILE_CACHE_DURATION);
        setProfile(data as UserProfile);
      } else {
        console.log("No profile found, creating default profile");
        
        try {
          // Get email from user object
          let userEmail = user?.email || '';
          
          const defaultProfile = {
            id: userId,
            email: userEmail,
            role: 'user' as UserProfile['role'],
            created_at: new Date().toISOString()
          };
          
          const { error: insertError, data: insertedProfile } = await supabase
            .from('profiles')
            .insert(defaultProfile)
            .select()
            .single();
          
          if (insertError) {
            console.error('Error creating default profile:', insertError);
            setAuthError(`Error creating default profile: ${insertError.message}`);
            return;
          }
          
          console.log("Created default profile:", insertedProfile);
          setProfile(insertedProfile as UserProfile);
          
          // Cache the new profile
          localStorageCache.set(PROFILE_CACHE_KEY, insertedProfile, PROFILE_CACHE_DURATION);
        } catch (createError: any) {
          console.error('Error in profile creation:', createError);
          setAuthError(`Failed to create user profile: ${createError.message}`);
        }
      }
    } catch (error: any) {
      console.error('Error in fetchUserProfile:', error);
      setAuthError(`Error in profile management: ${error.message}`);
    }
  };

  return {
    session,
    user,
    profile,
    isLoading,
    isConfigured,
    authError
  };
};
